/**
 * One order bought through our own checkout, in full.
 *
 * The list folds a multi-line order down to a single row and drops the
 * provider's payload; this is where the rest of it lives. Blocks that
 * would be nothing but dashes for a plain card sale — attribution, gift
 * details — are omitted rather than rendered empty, so what is on screen
 * is what actually happened.
 */
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Refund, type SiteOrderDetail } from '../api/client'
import { Panel } from '../components/Panel'
import { PubBadge } from '../components/PubBadge'
import { fmtCardExpiry, fmtDate, fmtDateTime, fmtMoney, providerLabel } from '../lib/format'
import { describeError } from '../lib/describeError'

export function SiteOrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [refundOpen, setRefundOpen] = useState(false)
  const [voidOpen, setVoidOpen] = useState(false)
  const [offlineRefundOpen, setOfflineRefundOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['site-order', id],
    queryFn: () => api.get<SiteOrderDetail>(`/api/orders/site/${id}`),
    enabled: Boolean(id),
  })

  if (isLoading) {
    return <div className="p-8 text-paper-trace">Loading order…</div>
  }

  if (error || !data) {
    return (
      <div className="p-8 space-y-4">
        <BackLink />
        <p className="text-paper-dim max-w-[62ch]">
          No such order. This page covers only what was bought through
          checkout on the member sites — an order that reached us from
          SimpleCirc lives on the subscriber's record instead.
        </p>
      </div>
    )
  }

  const provider = providerLabel(data.source)
  const utmSet = Object.values(data.utm).some(Boolean)
  const isGift = Boolean(data.gift_giver_account_id || data.gift_message)
  // A check has no gateway to send money back through, so it never gets
  // the gateway refund control. It gets the other two instead: void, for
  // a check that never cleared, and a record of the refund check we
  // write ourselves.
  const isOfflinePayment = data.source === 'offline' && data.kind === 'payment'
  // A refund order is money going BACK. Its page must not read as a
  // payment ("Amount paid", "Charged by"), offer a refund of itself, or
  // leave the operator without a way back to what it reversed.
  const isRefundOrder = data.kind === 'refund'
  const isVoid = isRefundOrder && !!data.refunded_order?.voided

  return (
    <div className="p-8 space-y-6 max-w-[1100px]">
      <BackLink />

      <header className="border-b border-ink-500 pb-6">
        <div className="label mb-1">
          {provider} · {data.kind === 'payment' ? 'order' : isVoid ? 'void' : data.kind}
        </div>
        <h1 className="font-serif text-[38px] leading-[1.05] tracking-tight text-paper">
          {fmtMoney(data.amount_paid, false, 2)}
          <span className="text-amber-glow"> ∙ </span>
          <span className="text-paper-trace">
            {data.payer_full_name || data.payer_email || 'unknown buyer'}
          </span>
        </h1>
        <p className="mt-2 font-mono text-[12px] text-paper-trace">{data.order_id}</p>
      </header>

      <Panel label={isVoid ? 'Void' : isRefundOrder ? 'Refund' : 'Payment'}>
        {isRefundOrder && (
          <p className="text-[13px] text-paper-dim max-w-[70ch] mb-4">
            {isVoid
              ? 'The charge was cancelled before it settled, so no money moved — the pending charge drops off the member\'s statement.'
              : 'Money sent back to the member.'}
            {data.refunded_order && (
              <>
                {' '}Reverses order{' '}
                <Link
                  to={`/orders/${data.refunded_order.id}`}
                  className="font-mono text-amber-glow hover:underline"
                >
                  {data.refunded_order.order_id}
                </Link>
                .
              </>
            )}
          </p>
        )}
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
          <Field label={isVoid ? 'Voided' : isRefundOrder ? 'Refunded' : 'Taken'}>{fmtDateTime(data.order_date)}</Field>
          {/* When the provider took the money vs when we heard about it.
              A wide gap is a late webhook, which is worth being able to see. */}
          <Field label="Recorded here">{fmtDateTime(data.ingested_at)}</Field>
          <Field label={isVoid ? 'Amount cancelled' : isRefundOrder ? 'Amount refunded' : 'Amount paid'}>
            {fmtMoney(data.amount_paid, false, 2)}
          </Field>
          {data.amount_due !== null && data.amount_due !== data.amount_paid && (
            <Field label="Amount due">{fmtMoney(data.amount_due, false, 2)}</Field>
          )}
          <Field label={isRefundOrder ? 'Originally paid with' : 'Paid with'}>
            {provider}
            {data.payment_type && data.payment_type !== 'paypal'
              ? ` · ${data.payment_type}`
              : ''}
          </Field>
          {data.last4 && (
            <Field label="Card">
              {data.card_type ?? 'card'} ••{data.last4}
              {data.card_expiry ? ` · exp ${fmtCardExpiry(data.card_expiry)}` : ''}
            </Field>
          )}
          {data.payment_source && !isRefundOrder && (
            <Field label="Charged by">
              {data.payment_source === 'auto_renewal' ? 'auto-renewal' : data.payment_source}
            </Field>
          )}
          {data.promo_code && (
            <Field label="Promo code">
              <span className="font-mono text-amber-glow">{data.promo_code}</span>
            </Field>
          )}
        </dl>
      </Panel>

      <Panel label="Buyer">
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
          <Field label="Name">{data.payer_full_name ?? '—'}</Field>
          <Field label="Email">
            {data.payer_email ? (
              <a className="hover:text-amber-glow hover:underline" href={`mailto:${data.payer_email}`}>
                {data.payer_email}
              </a>
            ) : '—'}
          </Field>
          {data.payer_account_id && (
            <Field label="Account">{data.payer_account_id}</Field>
          )}
        </dl>
      </Panel>

      <Panel label={`What was bought · ${data.lines.length} line${data.lines.length === 1 ? '' : 's'}`}>
        {data.lines.length === 0 ? (
          /* Money in with nothing attached to it is the interesting case,
             so it gets said out loud rather than shown as an empty table. */
          <p className="text-paper-dim max-w-[62ch]">
            This order has no lines — the payment landed but nothing was
            attributed to a title. Worth chasing.
          </p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-500">
                <Th>Title</Th>
                <Th>Type</Th>
                <Th>Term</Th>
                <Th>Subscriber</Th>
                <Th align="right">Amount</Th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l) => (
                <tr key={l.id} className="border-b border-ink-700/40">
                  <Td>
                    {l.publication_slug ? (
                      <PubBadge slug={l.publication_slug} compact />
                    ) : (
                      <span className="text-paper-trace">unattributed</span>
                    )}
                  </Td>
                  <Td subtle>{l.new_or_renewal ?? '—'}</Td>
                  <Td mono subtle>{l.term_months ? `${l.term_months} mo` : '—'}</Td>
                  <Td>
                    {l.subscriber_id ? (
                      <Link
                        to={`/subscribers/${l.subscriber_id}`}
                        className="text-paper hover:text-amber-glow hover:underline"
                      >
                        #{l.subscriber_id}
                      </Link>
                    ) : (
                      <span className="label-amber">unmatched</span>
                    )}
                  </Td>
                  <Td mono align="right">{fmtMoney(l.amount_paid, false, 2)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {utmSet && (
        <Panel label="How they got here">
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
            <Field label="Source">{data.utm.source ?? '—'}</Field>
            <Field label="Medium">{data.utm.medium ?? '—'}</Field>
            <Field label="Campaign">{data.utm.campaign ?? '—'}</Field>
            {data.utm.term && <Field label="Term">{data.utm.term}</Field>}
            {data.utm.content && <Field label="Content">{data.utm.content}</Field>}
          </dl>
        </Panel>
      )}

      {isGift && (
        <Panel label="Gift">
          <dl className="grid grid-cols-1 gap-y-3">
            {data.gift_giver_account_id && (
              <Field label="Given by">{data.gift_giver_account_id}</Field>
            )}
            {data.lines.some((l) => l.gift_recipient_account_id) && (
              <Field label="Given to">
                {data.lines
                  .map((l) => l.gift_recipient_account_id)
                  .filter(Boolean)
                  .join(', ')}
              </Field>
            )}
            {data.gift_message && (
              <Field label="Message">
                <span className="whitespace-pre-wrap">{data.gift_message}</span>
              </Field>
            )}
          </dl>
        </Panel>
      )}

      {isOfflinePayment ? (
        <>
          <VoidPanel order={data} onVoid={() => setVoidOpen(true)} />
          <OfflineRefundPanel
            order={data}
            onRefund={() => setOfflineRefundOpen(true)}
          />
        </>
      ) : data.source !== 'offline' && !isRefundOrder ? (
        <RefundPanel
          order={data}
          provider={provider}
          onRefund={() => setRefundOpen(true)}
        />
      ) : null}

      <RawPayloadPanel provider={provider} payload={data.raw_payload} />

      {/* Page level, never inside a Panel. Panel carries backdrop-blur,
          and a backdrop-filter makes an element the containing block AND
          the stacking context for its fixed-position descendants — so a
          modal nested in one is sized to the panel instead of the
          viewport, and painted under the panel that follows it. That is
          what made the dialog's buttons unclickable. */}
      {refundOpen && (
        <RefundDialog
          order={data}
          provider={provider}
          refundable={data.refundable_amount ?? 0}
          onClose={() => setRefundOpen(false)}
          onDone={() => {
            setRefundOpen(false)
            queryClient.invalidateQueries({ queryKey: ['site-order'] })
            queryClient.invalidateQueries({ queryKey: ['site-orders'] })
          }}
        />
      )}

      {voidOpen && (
        <VoidDialog
          order={data}
          onClose={() => setVoidOpen(false)}
          onDone={() => {
            setVoidOpen(false)
            queryClient.invalidateQueries({ queryKey: ['site-order'] })
            queryClient.invalidateQueries({ queryKey: ['site-orders'] })
          }}
        />
      )}

      {offlineRefundOpen && (
        <OfflineRefundDialog
          order={data}
          onClose={() => setOfflineRefundOpen(false)}
          onDone={() => {
            setOfflineRefundOpen(false)
            queryClient.invalidateQueries({ queryKey: ['site-order'] })
            queryClient.invalidateQueries({ queryKey: ['site-orders'] })
          }}
        />
      )}
    </div>
  )
}

function BackLink() {
  return (
    <Link to="/orders" className="label-amber hover:underline">
      ← Orders
    </Link>
  )
}

function RawPayloadPanel({
  provider, payload,
}: {
  provider: string
  payload: Record<string, unknown> | null
}) {
  const [open, setOpen] = useState(false)
  const entries = Object.entries(payload ?? {}).sort(([a], [b]) => a.localeCompare(b))
  if (entries.length === 0) return null

  return (
    <Panel
      accent="violet"
      label={`What ${provider} sent`}
      right={
        <button
          onClick={() => setOpen(!open)}
          className="font-mono text-[10px] uppercase tracking-label text-amber-glow hover:underline"
        >
          {open ? '▾ hide' : `▸ show all ${entries.length} fields`}
        </button>
      }
    >
      {/* Panel's bodyClassName appends to its default p-4 rather than
          replacing it, so a collapsed panel cannot be made flush — it
          just renders an empty padded box. Rather than fight that here,
          the collapsed state says something useful. */}
      <p className="text-[13px] text-paper-dim">
        {entries.length} field{entries.length === 1 ? '' : 's'} recorded from
        the {provider} payload{open ? ':' : '.'}
      </p>

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 mt-3">
          {entries.map(([k, v]) => {
            const text = v === null || v === undefined
              ? '—'
              : typeof v === 'object' ? JSON.stringify(v) : String(v)
            return (
              <div
                key={k}
                className="flex items-baseline justify-between gap-3 border-b border-ink-500/30 pb-1"
              >
                <span className="font-mono text-[10px] uppercase tracking-label text-paper-faint shrink-0 max-w-[50%]">
                  {k}
                </span>
                <span
                  className="font-mono text-[11px] text-paper-dim text-right truncate"
                  title={text}
                >
                  {text}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </Panel>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="label text-paper-faint mb-0.5">{label}</dt>
      <dd className="text-[13px] text-paper">{children}</dd>
    </div>
  )
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'right' }) {
  return (
    <th className={`label py-2 px-4 font-normal ${align === 'right' ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  )
}

function Td({
  children, mono, subtle, align,
}: {
  children: React.ReactNode
  mono?: boolean
  subtle?: boolean
  align?: 'right'
}) {
  return (
    <td
      className={`py-2 px-4 text-[13px] ${mono ? 'font-mono' : ''} ${
        subtle ? 'text-paper-dim' : 'text-paper'
      } ${align === 'right' ? 'text-right' : ''}`}
    >
      {children}
    </td>
  )
}

function RefundPanel({
  order, provider, onRefund,
}: {
  order: SiteOrderDetail
  provider: string
  onRefund: () => void
}) {
  const refundable = order.refundable_amount
  const canRefund = refundable !== null && refundable > 0

  return (
    <Panel
      label="Refund"
      right={
        <button
          type="button"
          disabled={!canRefund}
          onClick={onRefund}
          className="font-mono text-[10px] uppercase tracking-label text-amber-glow hover:underline disabled:opacity-30 disabled:no-underline"
        >
          Refund…
        </button>
      }
    >
      {/* Three states, and they must not be confused: we could not ask,
          there is nothing left, or here is what is left. */}
      {refundable === null ? (
        <p className="text-paper-dim max-w-[62ch]">
          Couldn't reach {provider} to check what's refundable. The order is
          fine — try again in a moment.
        </p>
      ) : refundable === 0 ? (
        <p className="text-paper-dim max-w-[62ch]">
          Nothing left to refund on this order.
        </p>
      ) : (
        <p className="text-[13px] text-paper">
          {fmtMoney(refundable, false, 2)} still refundable
          {refundable < (order.amount_paid ?? 0) && (
            <span className="text-paper-trace">
              {' '}of {fmtMoney(order.amount_paid, false, 2)}
            </span>
          )}
        </p>
      )}

      {order.refunds.length > 0 && (
        <table className="w-full mt-4">
          <thead>
            <tr className="border-b border-ink-500">
              <Th>When</Th>
              <Th>Status</Th>
              <Th>Reason</Th>
              <Th align="right">Amount</Th>
            </tr>
          </thead>
          <tbody>
            {order.refunds.map((r) => <RefundRow key={r.id} refund={r} />)}
          </tbody>
        </table>
      )}

    </Panel>
  )
}

function RefundRow({ refund: r }: { refund: Refund }) {
  return (
    <tr className="border-b border-ink-700/40">
      <Td mono subtle>{fmtDateTime(r.completed_at ?? r.created_at)}</Td>
      <Td>
        <span className={r.status === 'succeeded' ? 'text-paper' : 'label-amber'}>
          {r.status === 'succeeded' && r.voided ? 'voided' : r.status}
        </span>
        {/* Same money outcome for the member, different paperwork: a
            void never settled, so no refund credit will ever appear. */}
        {r.status === 'succeeded' && r.voided && (
          <div className="text-[11px] text-paper-trace mt-0.5 max-w-[48ch]">
            cancelled before it settled — no money moved
          </div>
        )}
        {/* A succeeded refund can still carry an error: the money moved
            but the follow-up cancel did not. Someone has to finish it. */}
        {r.error && (
          <div className="text-[11px] text-radar-red mt-0.5 max-w-[48ch]">
            {r.error}
          </div>
        )}
      </Td>
      <Td subtle>{r.reason ?? '—'}</Td>
      <Td mono align="right">{fmtMoney(r.amount, false, 2)}</Td>
    </tr>
  )
}

function RefundDialog({
  order, provider, refundable, onClose, onDone,
}: {
  order: SiteOrderDetail
  provider: string
  refundable: number
  onClose: () => void
  onDone: () => void
}) {
  const [amount, setAmount] = useState(refundable.toFixed(2))
  const [reason, setReason] = useState('')
  // Follows the amount until the operator says otherwise. Deriving it
  // once at open would leave the box ticked after someone reduced a full
  // refund to a goodwill one — cancelling a membership they meant to keep.
  const [override, setOverride] = useState<boolean | null>(null)
  const [endMembership, setEndMembership] = useState(false)

  const submit = useMutation({
    mutationFn: () =>
      api.post<Refund>(`/api/orders/site/${order.id}/refund`, {
        amount,
        reason: reason.trim() || null,
        cancel_billing: cancelBilling,
        cancel_subscription: endMembership,
      }),
    onSuccess: onDone,
  })

  const parsed = Number(amount)
  const valid = Number.isFinite(parsed) && parsed > 0 && parsed <= refundable
  const isFull = valid && parsed === order.amount_paid
  // Ending the membership implies stopping the renewals, so the billing
  // box follows it and stops being the operator's to argue with.
  const cancelBilling = endMembership || (override ?? isFull)

  // A BOGO order's lines span more than one member — the friend's line
  // rides the same order, at $0, alongside the buyer's paid one (see
  // checkout_card.py / webhooks/paypal.py's shared-order design).
  // `_cancel_billing` acts on every line this order has, not just the
  // one an operator has in mind — on the card rail because the friend's
  // row shares the buyer's vaulted card, on PayPal because it's the
  // SAME agreement id. Either way, "cancel future billing" here ends
  // BOTH memberships, and the operator needs to see that before clicking.
  const otherMemberships = Array.from(
    new Map(
      order.lines
        .filter((l) => l.subscriber_id != null)
        .map((l) => [
          l.subscriber_id,
          `${l.publication_name ?? l.publication_slug ?? 'unknown title'} (${l.subscriber_name ?? 'member'})`,
        ]),
    ).values(),
  )
  const isMultiMembership = otherMemberships.length > 1

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-ink-700 border border-ink-500 p-6 w-[520px] space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-[24px] text-paper">
          Refund this order
        </h2>

        <div>
          <div className="label mb-1">Amount (max {fmtMoney(refundable, false, 2)})</div>
          <input
            className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[13px]"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
          />
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={cancelBilling}
            disabled={endMembership}
            onChange={(e) => setOverride(e.target.checked)}
            className="mt-1 accent-amber-glow disabled:opacity-50"
          />
          <span className="text-[13px] text-paper">
            Cancel future billing
            <span className="block text-[12px] text-paper-trace">
              {endMembership
                ? 'Implied by ending the membership.'
                : isFull
                  ? 'Usual for a full refund — they asked to leave.'
                  : 'Not usual for a partial refund — a goodwill adjustment normally leaves the membership running.'}
            </span>
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={endMembership}
            onChange={(e) => setEndMembership(e.target.checked)}
            className="mt-1 accent-radar-red"
          />
          <span className="text-[13px] text-paper">
            Cancel the subscription
            <span className="block text-[12px] text-paper-trace">
              Ends the membership today. They lose access straight away
              rather than reading out the period they had paid for.
            </span>
          </span>
        </label>

        <div>
          <div className="label mb-1">Reason (optional, kept on the record)</div>
          <input
            className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper text-[13px]"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. member asked to cancel"
          />
        </div>

        {/* Spelled out rather than implied. Whoever clicks should not
            have to know the policy to predict what happens. */}
        <div className="border border-ink-500 bg-ink-800/60 p-3 text-[13px] text-paper-dim space-y-1">
          <p>
            {provider} sends {valid ? fmtMoney(parsed, false, 2) : 'the amount'}{' '}
            back to the card on file.
          </p>
          {endMembership ? (
            <p>
              The membership <span className="text-paper">ends today</span>.
              They lose access immediately and are told so by email.
            </p>
          ) : cancelBilling ? (
            <p>
              Future billing is <span className="text-paper">cancelled</span>.
              Access continues until the end of the period already paid for.
            </p>
          ) : (
            <p>
              Billing continues — they stay a member on the next cycle.
            </p>
          )}
          {cancelBilling && isMultiMembership && (
            <p className="text-radar-amber">
              This order funds more than one membership — cancelling billing
              here ends renewals for all of them: {otherMemberships.join(', ')}.
            </p>
          )}
          <p className="text-paper-trace">This cannot be undone here.</p>
        </div>

        {submit.isError && (
          <p className="text-radar-red text-[13px]">
            {submit.error instanceof Error
              ? describeError(submit.error)
              : 'The refund did not go through.'}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="label hover:underline"
            disabled={submit.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => submit.mutate()}
            disabled={!valid || submit.isPending}
            className="border border-amber-glow text-amber-glow px-4 py-2 font-mono text-[12px] uppercase tracking-label hover:bg-amber-glow/10 disabled:opacity-30"
          >
            {submit.isPending
              ? 'Refunding…'
              : `Refund ${valid ? fmtMoney(parsed, false, 2) : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}

function VoidPanel({
  order, onVoid,
}: {
  order: SiteOrderDetail
  onVoid: () => void
}) {
  // A check order carries exactly one line — `void_check_payment` itself
  // reads only the first — so this is the term the void would reverse.
  const line = order.lines[0]
  const canVoid = !order.voided && Boolean(line?.term_months)

  return (
    <Panel
      label="Void"
      right={
        <button
          type="button"
          disabled={!canVoid}
          onClick={onVoid}
          className="font-mono text-[10px] uppercase tracking-label text-radar-red hover:underline disabled:opacity-30 disabled:no-underline"
        >
          Void — check did not clear
        </button>
      }
    >
      {order.voided ? (
        <p className="text-paper-dim max-w-[62ch]">
          This check has already been voided.
        </p>
      ) : !line?.term_months ? (
        <p className="text-paper-dim max-w-[62ch]">
          This order has no recorded term, so it can't be unwound
          automatically here — correct the member's expiry by hand instead.
        </p>
      ) : (
        <p className="text-[13px] text-paper">
          A check that never cleared. Voiding takes back the{' '}
          {line.term_months} month{line.term_months === 1 ? '' : 's'} it
          granted.
        </p>
      )}
    </Panel>
  )
}

function VoidDialog({
  order, onClose, onDone,
}: {
  order: SiteOrderDetail
  onClose: () => void
  onDone: () => void
}) {
  const [reason, setReason] = useState('')
  const line = order.lines[0]
  const months = line?.term_months ?? 0
  // Whoever's access actually ends, which on a gift is NOT the payer:
  // `access_ends_if_voided` below is computed from this line's
  // subscriber, and 58% of this cohort are gifts. Naming the payer read
  // "John Smith's access ends…" when John paid and it is Jane's term
  // going back — on a destructive action, behind a confirmation.
  const name = line?.subscriber_name
    || order.payer_full_name || order.payer_email || 'This member'

  const submit = useMutation({
    mutationFn: () =>
      api.post(`/api/offline/void`, {
        order_key: order.order_id,
        reason: reason.trim(),
      }),
    onSuccess: onDone,
  })

  const valid = reason.trim().length > 0

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-ink-700 border border-ink-500 p-6 w-[520px] space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-[24px] text-paper">
          Void this check
        </h2>

        {/* Spelled out rather than implied, same as the refund dialog —
            whoever clicks should not have to know the policy to predict
            what happens. */}
        <div className="border border-ink-500 bg-ink-800/60 p-3 text-[13px] text-paper-dim space-y-1">
          <p>
            This reverses {months} month{months === 1 ? '' : 's'}.{' '}
            <span className="text-paper">{name}</span>'s access ends{' '}
            <span className="text-paper">
              {line?.access_ends_if_voided
                ? fmtDate(line.access_ends_if_voided)
                : 'today'}
            </span>.
          </p>
          <p className="text-paper-trace">This cannot be undone here.</p>
        </div>

        <div>
          <div className="label mb-1">Reason (required, kept on the record)</div>
          <input
            className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper text-[13px]"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. returned NSF"
          />
        </div>

        {submit.isError && (
          <p className="text-radar-red text-[13px]">
            {submit.error instanceof Error
              ? describeError(submit.error)
              : 'The void did not go through.'}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="label hover:underline"
            disabled={submit.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => submit.mutate()}
            disabled={!valid || submit.isPending}
            className="border border-radar-red text-radar-red px-4 py-2 font-mono text-[12px] uppercase tracking-label hover:bg-radar-red/10 disabled:opacity-30"
          >
            {submit.isPending ? 'Voiding…' : 'Void check'}
          </button>
        </div>
      </div>
    </div>
  )
}


function OfflineRefundPanel({
  order, onRefund,
}: {
  order: SiteOrderDetail
  onRefund: () => void
}) {
  const sent = order.offline_refunds.reduce((t, r) => t + (r.amount ?? 0), 0)
  const paid = order.amount_paid ?? 0
  const left = Math.max(paid - sent, 0)
  // A voided check is one that never cleared — the money never arrived,
  // so there is nothing to send back and the backend refuses. Nothing is
  // left to send back either once the whole cheque has been returned.
  const canRefund = !order.voided && left > 0

  return (
    <Panel
      label="Refund check"
      right={
        <button
          type="button"
          disabled={!canRefund}
          onClick={onRefund}
          className="font-mono text-[10px] uppercase tracking-label text-amber-glow hover:underline disabled:opacity-30 disabled:no-underline"
        >
          Record a refund check…
        </button>
      }
    >
      {order.voided ? (
        <p className="text-paper-dim max-w-[62ch]">
          This check was voided — it never cleared, so no money arrived to
          send back.
        </p>
      ) : (
        <p className="text-[13px] text-paper max-w-[62ch]">
          {left > 0
            ? <>{fmtMoney(left, false, 2)} of {fmtMoney(paid, false, 2)} not yet sent back.</>
            : <>The whole {fmtMoney(paid, false, 2)} has been sent back.</>}{' '}
          {/* The distinction the operator has to hold: nothing here moves
              money. Someone writes a physical check and this is the book
              entry for it. */}
          <span className="text-paper-trace">
            Recording one here books a check you write by hand — it sends
            nothing itself, and leaves the membership running.
          </span>
        </p>
      )}

      {order.offline_refunds.length > 0 && (
        <table className="w-full mt-4">
          <thead>
            <tr className="border-b border-ink-500">
              <Th>When</Th>
              <Th>Check no.</Th>
              <Th>Reason</Th>
              <Th align="right">Amount</Th>
            </tr>
          </thead>
          <tbody>
            {order.offline_refunds.map((r) => (
              <tr key={r.order_id} className="border-b border-ink-700/40">
                <Td mono subtle>{fmtDateTime(r.order_date)}</Td>
                <Td mono>{r.refund_check_number ?? '—'}</Td>
                <Td subtle>{r.reason ?? '—'}</Td>
                <Td mono align="right">{fmtMoney(r.amount, false, 2)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )
}

function OfflineRefundDialog({
  order, onClose, onDone,
}: {
  order: SiteOrderDetail
  onClose: () => void
  onDone: () => void
}) {
  const sent = order.offline_refunds.reduce((t, r) => t + (r.amount ?? 0), 0)
  const left = Math.max((order.amount_paid ?? 0) - sent, 0)
  const [amount, setAmount] = useState(left.toFixed(2))
  const [checkNumber, setCheckNumber] = useState('')
  const [reason, setReason] = useState('')

  const parsed = Number(amount)
  const amountValid = Number.isFinite(parsed) && parsed > 0 && parsed <= left
  const valid = amountValid && checkNumber.trim().length > 0 && reason.trim().length > 0

  const submit = useMutation({
    mutationFn: () =>
      api.post('/api/offline/refund', {
        order_key: order.order_id,
        // Cents, so nothing a human writes on a check goes through a
        // float on the way to the ledger.
        amount_cents: Math.round(parsed * 100),
        refund_check_number: checkNumber.trim(),
        reason: reason.trim(),
      }),
    onSuccess: onDone,
  })

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-ink-700 border border-ink-500 p-6 w-[520px] space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-[24px] text-paper">
          Record a refund check
        </h2>

        {/* Said first, and plainly. Everything else on this page that
            says "refund" sends money; this one does not, and an operator
            who assumes it did would never post the cheque. */}
        <div className="border border-amber-glow/40 bg-amber-glow/5 p-3 text-[13px] text-paper space-y-1">
          <p>
            This <span className="text-amber-glow">records</span> a check you
            write and post yourself. Nothing is sent from here — there is no
            gateway behind a check to send it through.
          </p>
        </div>

        <div>
          <div className="label mb-1">
            Amount (max {fmtMoney(left, false, 2)})
          </div>
          <input
            className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[13px]"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
          />
        </div>

        <div>
          <div className="label mb-1">Our check number (required)</div>
          <input
            className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper font-mono text-[13px]"
            value={checkNumber}
            onChange={(e) => setCheckNumber(e.target.value)}
            placeholder="the number on the check you are writing"
          />
          {/* Why it is required: it is half the key that stops the same
              outgoing check being booked twice. */}
          <p className="text-[12px] text-paper-trace mt-1">
            Keyed against this order, so the same check can't be recorded
            twice.
          </p>
        </div>

        <div>
          <div className="label mb-1">Reason (required, kept on the record)</div>
          <input
            className="w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper text-[13px]"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. member asked to cancel"
          />
        </div>

        <div className="border border-ink-500 bg-ink-800/60 p-3 text-[13px] text-paper-dim space-y-1">
          <p>
            The membership is <span className="text-paper">left running</span>.
            Whether they keep what they were sold is your separate call —
            end it on their record if that is the intent.
          </p>
          <p>
            A check that <span className="text-paper">never cleared</span> is
            a void, not this: that takes the term back with it.
          </p>
        </div>

        {submit.isError && (
          <p className="text-radar-red text-[13px]">
            {submit.error instanceof Error
              ? describeError(submit.error)
              : 'The refund check was not recorded.'}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="label hover:underline"
            disabled={submit.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => submit.mutate()}
            disabled={!valid || submit.isPending}
            className="border border-amber-glow text-amber-glow px-4 py-2 font-mono text-[12px] uppercase tracking-label hover:bg-amber-glow/10 disabled:opacity-30"
          >
            {submit.isPending
              ? 'Recording…'
              : `Record ${amountValid ? fmtMoney(parsed, false, 2) : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}
